import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpService from "@shared/services/http.service";
import { API_CONFIG } from "@shared/constants/api";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface Country {
  id: number;
  country_name: string;
  country_image_url: string;
  country_image: string;
  presignedUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface CountryState {
  countries: Country[];
  loading: boolean;
  creating: boolean;
  uploading: boolean;
  deleting: boolean;
  error: string | null;
}

interface CreateCountryPayload {
  country_name: string;
  country_image: File;
}

interface UploadResponse {
  filename: string;
  presignedUrl: string;
}

/* -------------------------------------------------------------------------- */
/*                               Initial State                                */
/* -------------------------------------------------------------------------- */

const initialState: CountryState = {
  countries: [],
  loading: false,
  creating: false,
  uploading: false,
  deleting: false,
  error: null,
};

/* -------------------------------------------------------------------------- */
/*                               Async Thunks                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch all countries
 * GET: /country/all
 */
export const fetchCountries = createAsyncThunk<Country[]>(
  "country/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpService.get(API_CONFIG.path.country_all);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch countries");
    }
  }
);

/**
 * Upload country image
 * POST: /file-upload/single
 */
export const uploadCountryImage = createAsyncThunk<UploadResponse, File>(
  "country/uploadImage",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpService.post(API_CONFIG.path.image_upload, formData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to upload image");
    }
  }
);

/**
 * Create country (combined flow: upload image + create)
 * 1. Upload image → get filename
 * 2. Create country with filename
 * 3. Refresh countries list
 */
export const createCountry = createAsyncThunk<Country, CreateCountryPayload>(
  "country/create",
  async ({ country_name, country_image }, { dispatch, rejectWithValue }) => {
    try {
      // Step 1: Upload image
      const uploadResult = await dispatch(uploadCountryImage(country_image)).unwrap();

      // Step 2: Create country with filename
      const response = await httpService.post(API_CONFIG.path.country_create, {
        country_name,
        country_image: uploadResult.filename,
      });

      // Step 3: Refresh countries list
      await dispatch(fetchCountries());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create country");
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                  Slice                                     */
/* -------------------------------------------------------------------------- */

const countrySlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    clearCountryError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Countries
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action: PayloadAction<Country[]>) => {
        state.loading = false;
        state.countries = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Upload Country Image
      .addCase(uploadCountryImage.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadCountryImage.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadCountryImage.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload as string;
      })

      // Create Country
      .addCase(createCountry.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createCountry.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createCountry.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      })

      // Update Country
      .addCase(updateCountry.pending, (state) => {
        state.creating = true; // Reuse creating for update loading
        state.error = null;
      })
      .addCase(updateCountry.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(updateCountry.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      })

      // Delete Country
      .addCase(deleteCountry.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteCountry.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteCountry.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });
  },
});

/**
 * Update country (combined flow: optional upload + update)
 * If image provided: upload → get filename → update
 * If no image: just update with existing filename
 */
export const updateCountry = createAsyncThunk<
  Country,
  { id: number; country_name: string; country_image?: File; existing_image?: string, }
>(
  "country/update",
  async ({ id, country_name, country_image, existing_image }, { dispatch, rejectWithValue }) => {
    try {
      let imageFilename = existing_image || "";

      // Upload new image if provided
      if (country_image) {
        const uploadResult = await dispatch(uploadCountryImage(country_image)).unwrap();
        imageFilename = uploadResult.filename;
      }

      // Update country
      const response = await httpService.put(`${API_CONFIG.path.country_update}/${id}`, {
        country_name,
        country_image: imageFilename,
      });

      // Refresh countries list
      await dispatch(fetchCountries());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update country");
    }
  }
);

/**
 * Delete country
 * DELETE: /country/delete/{id}
 */
export const deleteCountry = createAsyncThunk<void, number>(
  "country/delete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await httpService.deleteRequest(`${API_CONFIG.path.country_delete}/${id}`);

      // Refresh countries list
      await dispatch(fetchCountries());
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete country");
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                  Exports                                   */
/* -------------------------------------------------------------------------- */

export const { clearCountryError } = countrySlice.actions;

export default countrySlice.reducer;
